/* eslint-disable @next/next/no-img-element */
"use client";

import Image from "next/image";
import { ubuntu } from "@/globalConstants";

export default function HeroSection() {
  return (
    <section className="relative w-full min-h-[88vh] pb-24 bg-[#05574E]">

      {/* Hero image: one <picture>, so each device downloads only its own copy
          (two priority <Image>s downloaded both, about 4 MB, on every device).
          Lossless WebP: every visible pixel matches the original PNG. */}
      <div className="absolute inset-0 md:left-auto md:w-[55%] lg:w-[50%]">
        <picture>
          <source media="(min-width: 768px)" srcSet="/homepage-hero-icon.webp" width={1562} height={1952} />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/homepage-hero-mobile.webp"
            alt="Hero Background"
            width={720}
            height={1432}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-contain object-center md:object-cover"
          />
        </picture>
      </div>

      {/* Desktop edge gradients over the image */}
      <div className="absolute inset-y-0 right-0 w-[60%] md:w-[55%] lg:w-[50%] hidden md:block">
        <div className="absolute inset-0 bg-gradient-to-l from-[#05574E]/5 via-[#05574E]/40 to-[#05574E]"></div>
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#05574E] to-transparent"></div>
        <div className="absolute top-0 right-0 h-full w-20 bg-gradient-to-l from-[#05574E]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#05574E] to-transparent"></div>
      </div>

      {/* The logo now lives in <HomeHeader />, which overlays this section. */}

      {/* Content */}
      <div className="relative z-10 px-2 md:px-20 lg:px-24 pt-32 md:pt-60 pb-8 md:pb-20 max-w-8xl">

        {/* Heading — desktop restored, mobile fixed */}
        <h1
          className={`text-5xl md:text-7xl ${ubuntu.className} font-extrabold leading-tight text-[#f5e7c8]`}
        >
          {/* One copy of the text, with breakpoint-specific line breaks: two
              copies (one per breakpoint) put the H1 text in the page twice. */}
          Your everyday <br className="md:hidden" />
          guide to <br className="hidden md:inline" />
          Hindu <br className="md:hidden" />
          lifestyle
        </h1>

        <p className={`mt-4 text-lg md:text-4xl max-w-4xl ${ubuntu.className} text-[#f5e7c8]/90 leading-relaxed`}>
          Dharmayana helps you stay connected to tradition in the flow
          of modern life.
        </p>

        {/* Store Buttons */}
        <div className="flex items-center gap-4 mt-8">
          <a
            href="https://play.google.com/store/apps/details?id=in.dharmayana.android&pcampaignid=web_share"
            target="_blank"
          >
            <Image
              loading="eager"
              id="play-store-header"
              src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/Play Store Badge.webp`}
              height={30}
              width={180}
              alt="Download the app for Android"
              className="h-auto hover:scale-110 transition delay-150 duration-300 ease-in-out"
            />
          </a>
          <a
            href="https://apps.apple.com/in/app/dharmayana-hindu-app/id6474740467"
            target="_blank"
          >
            <Image
              loading="eager"
              id="app-store-footer"
              src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/iOS Badge.webp`}
              height={30}
              width={180}
              alt="Download the app for iOS"
              className="h-[55px] w-[175px] hover:scale-110 transition delay-150 duration-300 ease-in-out"
            />
          </a>
        </div>

        {/* Divider */}
        <div className="mt-24 border-t border-white/30 w-full"></div>

        {/* Bottom Stats */}
        <div className={`${ubuntu.className} mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-16 text-white`}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center">
              <Image src="/users.svg" alt="Users Icon" width={30} height={30} />
            </div>
            <div>
              <p className="font-bold text-xl">1+ million</p>
              <p className="opacity-90 text-sm mt-1">
                iOS and Android app downloads
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center">
              <Image src="/homepage-star.svg" alt="Star Icon" width={30} height={30} />
            </div>
            <div>
              <p className="font-bold text-xl">4.7</p>
              <p className="opacity-90 text-sm mt-1">
                Highly rated on App Store & Play Store
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center">
              <Image src="/homepage-globe.svg" alt="Globe Icon" width={30} height={30} />
            </div>
            <div>
              <p className="font-bold text-xl">50+  Countries</p>
              <p className="opacity-90 text-sm mt-1">
                Serving the global diaspora
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
